import { ReactElement, useState } from "react";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Stack, useTheme } from "@mui/material";
import { ExperienceBuilderSelector } from "@src/store/slices/experienceBuilder";
import { useSelector } from "@src/store";

const SEEN_STORAGE_KEY = "experienceBuilderModeSelectorTooltipSeen";

const hasSeenTooltip = () => localStorage.getItem(SEEN_STORAGE_KEY) === "true";

const KeyChip = ({ children }: { children: string }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    sx={{
      width: "20px",
      height: "18px",
      borderRadius: 1,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    }}
  >
    {children}
  </Stack>
);

export const ModeSelectorTooltip = ({
  children,
  disabled,
}: {
  children: ReactElement;
  disabled: boolean;
}) => {
  const theme = useTheme();
  const mode = useSelector(ExperienceBuilderSelector.getMode);
  const [seen, setSeen] = useState(hasSeenTooltip);

  const dismiss = () => {
    localStorage.setItem(SEEN_STORAGE_KEY, "true");
    setSeen(true);
  };

  const open = !seen && !disabled && mode === "navigate";

  return (
    <IgTooltip
      open={open}
      placement="bottom"
      title={
        open ? (
          <Stack spacing={4} padding={2} sx={{ width: 280 }}>
            <Stack spacing={1}>
              <IgTypography fontWeight={600}>
                Switch to Select to edit
              </IgTypography>
              <IgTypography
                variant="body2"
                fontWeight={400}
                sx={{
                  "&&&&": {
                    fontSize: theme.typography.body2.fontSize,
                    color: theme.palette.text.interactive.dark.hover,
                  },
                }}
              >
                You're browsing in Navigate mode. Switch to Select to click any
                element and edit it.
              </IgTypography>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <KeyChip>N</KeyChip>
                <KeyChip>S</KeyChip>
                <IgTypography
                  variant="body2"
                  sx={{
                    "&&&&": {
                      fontSize: theme.typography.body2.fontSize,
                      color: theme.palette.text.interactive.dark.disabled,
                    },
                  }}
                >
                  keys to switch
                </IgTypography>
              </Stack>
              <IgButton
                variant="contained"
                size="small"
                onClick={dismiss}
                sx={{
                  backgroundColor:
                    theme.igBackgroundColors.interactive.neutral.default,
                  color: theme.palette.text.primary,
                  "&:hover": {
                    backgroundColor:
                      theme.igBackgroundColors.interactive.neutral.hover,
                  },
                }}
              >
                Got it
              </IgButton>
            </Stack>
          </Stack>
        ) : (
          ""
        )
      }
      slotProps={{
        tooltip: { sx: { p: 2, borderRadius: "6px" } },
      }}
    >
      {children}
    </IgTooltip>
  );
};
